import { useState, useRef, useEffect, useCallback } from 'react';
import { useKnowledgeBase } from '../contexts/KnowledgeBaseContext';
import { ArticleCategory } from '../types/knowledgeBase';
import type { Article, ArticleCategory as ArticleCategoryType } from '../types/knowledgeBase';
import type { IncidentCategory, RequestCategory } from '../types/ticket';

interface KBDeflectionResult {
  articles: Article[];
  isSearching: boolean;
  hasSearched: boolean;
  searchQuery: string;
}

interface UseKBDeflectionOptions {
  debounceMs?: number;
  maxResults?: number;
  minQueryLength?: number;
}

// Map ticket categories to KB article categories for better matching
const INCIDENT_TO_KB_CATEGORY: Partial<Record<string, ArticleCategoryType[]>> = {
  Hardware: [ArticleCategory.HARDWARE, ArticleCategory.PRINTER],
  Software: [ArticleCategory.O365, ArticleCategory.TEAMS, ArticleCategory.OUTLOOK, ArticleCategory.ONEDRIVE, ArticleCategory.SHAREPOINT],
  Network: [ArticleCategory.NETWORK, ArticleCategory.VPN],
  Access: [ArticleCategory.ACTIVE_DIRECTORY, ArticleCategory.MFA, ArticleCategory.PASSWORD],
  Email: [ArticleCategory.OUTLOOK, ArticleCategory.O365],
  Other: [],
};

const REQUEST_TO_KB_CATEGORY: Partial<Record<string, ArticleCategoryType[]>> = {
  'New Hardware': [ArticleCategory.HARDWARE],
  'Software Install': [ArticleCategory.O365, ArticleCategory.TEAMS],
  'Access Request': [ArticleCategory.ACTIVE_DIRECTORY, ArticleCategory.MFA],
  'Account Setup': [ArticleCategory.ACTIVE_DIRECTORY, ArticleCategory.PASSWORD],
  'Equipment Return': [ArticleCategory.HARDWARE],
  Other: [],
};

/**
 * Hook for searching knowledge base articles based on user input during ticket creation.
 * Implements smart deflection by finding relevant articles that might solve the user's issue.
 */
export function useKBDeflection(options: UseKBDeflectionOptions = {}): {
  result: KBDeflectionResult;
  search: (query: string, category?: IncidentCategory | RequestCategory | null, isIncident?: boolean) => void;
  clearSearch: () => void;
} {
  const { debounceMs = 300, maxResults = 5, minQueryLength = 3 } = options;
  const { articles } = useKnowledgeBase();

  const [result, setResult] = useState<KBDeflectionResult>({
    articles: [],
    isSearching: false,
    hasSearched: false,
    searchQuery: '',
  });

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchRef = useRef<string>('');

  const performSearch = useCallback(
    (query: string, category?: IncidentCategory | RequestCategory | null, isIncident?: boolean) => {
      const normalizedQuery = query.toLowerCase().trim();

      if (normalizedQuery.length < minQueryLength) {
        setResult({
          articles: [],
          isSearching: false,
          hasSearched: false,
          searchQuery: normalizedQuery,
        });
        return;
      }

      // Get relevant KB categories based on ticket category
      let relevantKBCategories: ArticleCategory[] = [];
      if (category) {
        const categoryMap = isIncident ? INCIDENT_TO_KB_CATEGORY : REQUEST_TO_KB_CATEGORY;
        relevantKBCategories = categoryMap[category] ?? [];
      }

      // Search through published articles
      const publishedArticles = articles.filter((article) => article.isPublished);

      // Score each article based on relevance
      const scoredArticles = publishedArticles
        .map((article) => {
          let score = 0;

          // Title match (highest weight)
          if (article.title.toLowerCase().includes(normalizedQuery)) {
            score += 100;
            // Bonus for title starting with query
            if (article.title.toLowerCase().startsWith(normalizedQuery)) {
              score += 50;
            }
          }

          // Description match
          if (article.description.toLowerCase().includes(normalizedQuery)) {
            score += 50;
          }

          // Tag match
          const matchingTags = article.tags.filter((tag) =>
            tag.toLowerCase().includes(normalizedQuery)
          );
          score += matchingTags.length * 30;

          // Category relevance boost
          if (relevantKBCategories.includes(article.category)) {
            score += 25;
          }

          // Content-specific matching
          if (article.articleContent) {
            switch (article.articleContent.type) {
              case 'how-to':
                article.articleContent.content.steps.forEach((step) => {
                  if (step.title.toLowerCase().includes(normalizedQuery)) {
                    score += 20;
                  }
                  if (step.content.toLowerCase().includes(normalizedQuery)) {
                    score += 10;
                  }
                });
                break;

              case 'known-error':
                if (article.articleContent.content.symptoms.toLowerCase().includes(normalizedQuery)) {
                  score += 40;
                }
                if (article.articleContent.content.workaround.toLowerCase().includes(normalizedQuery)) {
                  score += 30;
                }
                article.articleContent.content.affectedSystems.forEach((system) => {
                  if (system.toLowerCase().includes(normalizedQuery)) {
                    score += 25;
                  }
                });
                break;

              case 'sop-runbook':
                if (article.articleContent.content.objective.toLowerCase().includes(normalizedQuery)) {
                  score += 30;
                }
                article.articleContent.content.prerequisites.forEach((prereq) => {
                  if (prereq.toLowerCase().includes(normalizedQuery)) {
                    score += 15;
                  }
                });
                break;
            }
          }

          // Known-error articles get a boost for incident tickets
          if (isIncident && article.articleType === 'known-error') {
            score += 20;
          }

          // How-to articles get a boost for request tickets
          if (!isIncident && article.articleType === 'how-to') {
            score += 15;
          }

          return { article, score };
        })
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, maxResults)
        .map(({ article }) => article);

      setResult({
        articles: scoredArticles,
        isSearching: false,
        hasSearched: true,
        searchQuery: normalizedQuery,
      });
    },
    [articles, maxResults, minQueryLength]
  );

  const search = useCallback(
    (query: string, category?: IncidentCategory | RequestCategory | null, isIncident?: boolean) => {
      const searchKey = `${query}-${category ?? ''}-${isIncident ?? ''}`;

      // Skip if same search
      if (searchKey === lastSearchRef.current) {
        return;
      }
      lastSearchRef.current = searchKey;

      // Clear any pending debounce
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Show searching state immediately
      setResult((prev) => ({
        ...prev,
        isSearching: true,
        searchQuery: query,
      }));

      // Debounce the actual search
      debounceTimer.current = setTimeout(() => {
        performSearch(query, category, isIncident);
      }, debounceMs);
    },
    [debounceMs, performSearch]
  );

  const clearSearch = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    lastSearchRef.current = '';
    setResult({
      articles: [],
      isSearching: false,
      hasSearched: false,
      searchQuery: '',
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return { result, search, clearSearch };
}

export type { KBDeflectionResult };
